import { quellZeit, rampeVon } from './rampe.js';
/**
 * **Ein angehefteter Bereich hält sich an seine Bilder** (7.166).
 *
 * Sascha, 24. September: „das mit den Klammern ist ganz gut für die Bereiche.
 * Aber ich sollte sie auch benennen können. Und irgendwie anheften oderso.
 * Damit wenn ich was verschiebe, ich es nicht aus dem Bereich heraus
 * schiebe.“
 *
 * Ein Bereich stand bis dahin auf der Uhr: von 12 bis 27 Sekunden, egal was
 * dort liegt. Fügte man davor etwas ein, rückte der Inhalt nach hinten und
 * der Bereich blieb stehen -- die Story schnitt dann mitten in etwas anderes.
 *
 * **Angeheftet hängt jede Kante an dem Bild, das an ihr steht**: an einem
 * Clip und einer Stelle in dessen Material, so wie Schnittprogramme Marken an
 * Clips hängen. Daraus folgt:
 * - rückt davor etwas ein, rückt der Bereich mit;
 * - kommt innen etwas dazu, wächst er, weil das Bild an der hinteren Kante
 *   nach hinten rückt;
 * - kürzt man einen Clip woanders, bleibt die Kante bei ihrem Bild;
 * - teilt man den Clip an der Kante, wandert sie mit in das Stück, das ihr
 *   Bild enthält.
 *
 * Der erste Versuch am 24. September hängte den Bereich an alle Clips, die
 * zur Hälfte in ihm lagen. Der Browser-Durchgang fand, dass eine Story aus
 * einer langen Aufnahme dann gar nichts hat, woran sie hängt -- das ist aber
 * der häufigste Fall.
 *
 * Gehängt wird an die unterste Bildspur, denn die trägt den Film; nur wo sie
 * leer ist, an irgendeine. Liegt an der Kante kein Clip, hängt sie am
 * nächsten und hält den Abstand.
 *
 * Gerechnet wird in `applyOp`, also auf dem Server und auf jedem Gerät
 * gleich; Rückgängig stellt die Clips wieder her und damit die Kanten.
 */
/**
 * Zeitleiste zu Material und zurück, **genau** -- auch mit Tempo-Rampe. Ein
 * mittleres Tempo reicht nicht: nach dem Teilen hat die linke Hälfte ein
 * anderes Mittel als der ganze Clip, und die Kante sprang (gemessen im Test:
 * 28,57 statt 20). Hin rechnet `quellZeit`, zurück eine Halbierung -- das
 * Material läuft nie rückwärts.
 */
function stelle(c, zeit) {
    const tau = Math.max(0, Math.min(c.duration, zeit - c.start));
    return c.sourceIn + quellZeit(tau, c.duration, rampeVon(c));
}
function zeitVon(c, quelle) {
    const r = rampeVon(c);
    const ziel = quelle - c.sourceIn;
    let lo = 0;
    let hi = c.duration;
    for (let i = 0; i < 60; i++) {
        const mitte = (lo + hi) / 2;
        if (quellZeit(mitte, c.duration, r) < ziel)
            lo = mitte;
        else
            hi = mitte;
    }
    return c.start + (lo + hi) / 2;
}
function kandidaten(t, b) {
    // Woran er hängen soll (7.196): ein bestimmter Clip, eine Spur -- oder,
    // wie seit 7.166, die unterste Bildspur. Was es nicht mehr gibt, zählt
    // nicht, und dann gilt wieder die unterste Bildspur.
    const clip = b?.heftenAn?.clip ? finde(t, b.heftenAn.clip) : undefined;
    if (clip)
        return [clip];
    const spur = b?.heftenAn?.spur ? t.tracks.find((tr) => tr.id === b.heftenAn.spur && tr.clips.length > 0) : undefined;
    if (spur)
        return spur.clips.slice().sort((x, y) => x.start - y.start);
    const basis = t.tracks.find((tr) => tr.kind === 'video' && tr.clips.length > 0);
    return (basis ? basis.clips : t.tracks.flatMap((tr) => tr.clips)).slice().sort((x, y) => x.start - y.start);
}
/** Der Clip, an den eine Kante bei `zeit` gehört. */
function ankerClip(t, zeit, kante, b) {
    const clips = kandidaten(t, b);
    if (clips.length === 0)
        return null;
    const drin = kante === 'von'
        ? clips.find((c) => c.start <= zeit + 1e-9 && zeit < c.start + c.duration - 1e-9)
        : clips.find((c) => c.start < zeit - 1e-9 && zeit <= c.start + c.duration + 1e-9);
    if (drin)
        return drin;
    // Keiner an der Stelle: der nächste in Richtung Bereich, sonst der andere.
    const danach = clips.find((c) => c.start >= zeit);
    const davor = [...clips].reverse().find((c) => c.start + c.duration <= zeit);
    return kante === 'von' ? (danach ?? davor ?? null) : (davor ?? danach ?? null);
}
/**
 * Liegt die Kante in einem Clip, hängt sie an seinem Bild (`quelle`); liegt
 * sie in einer Lücke, am Abstand zum nächsten Clip (`abstand`) -- ein Bild
 * gibt es dort nicht.
 */
function anker(t, zeit, kante, b) {
    const c = ankerClip(t, zeit, kante, b);
    if (!c)
        return undefined;
    const drin = zeit >= c.start - 1e-9 && zeit <= c.start + c.duration + 1e-9;
    return drin ? { clipId: c.id, quelle: stelle(c, zeit) } : { clipId: c.id, abstand: zeit - c.start };
}
/** Den Bereich an die Bilder heften, die jetzt an seinen Kanten stehen. */
export function bereichAnheften(t, b) {
    if (!b.angeheftet) {
        delete b.vonAnker;
        delete b.bisAnker;
        return;
    }
    b.vonAnker = anker(t, b.von, 'von', b);
    b.bisAnker = anker(t, b.bis, 'bis', b);
    if (!b.vonAnker)
        delete b.vonAnker;
    if (!b.bisAnker)
        delete b.bisAnker;
}
function finde(t, id) {
    for (const tr of t.tracks) {
        const c = tr.clips.find((x) => x.id === id);
        if (c)
            return c;
    }
    return undefined;
}
/** Enthält der Clip die Stelle in seinem Material (samt Rand)? */
function enthaelt(c, quelle) {
    const ende = c.sourceIn + quellZeit(c.duration, c.duration, rampeVon(c));
    return quelle >= c.sourceIn - 1e-6 && quelle <= ende + 1e-6;
}
/**
 * Clips haben sich geändert: die Kanten folgen ihren Bildern. Ist der Clip
 * einer Kante weg, bleibt sie stehen; liegt ihr Bild nicht mehr in ihm (weil
 * er geteilt wurde), hängt sie sich an das Stück, das jetzt an ihr steht.
 */
export function bereicheNachziehen(t) {
    let bewegt = false;
    for (const b of t.exportbereiche) {
        if (!b.angeheftet)
            continue;
        for (const kante of ['von', 'bis']) {
            const a = kante === 'von' ? b.vonAnker : b.bisAnker;
            const c = a ? finde(t, a.clipId) : undefined;
            // Ein Bild, das der Clip noch enthält, oder ein Abstand: die Kante folgt.
            // Ein Bild, das er nicht mehr enthält (geteilt, gekürzt), wird nicht
            // hochgerechnet -- mit Tempo-Rampe hätte die linke Hälfte ein anderes
            // Tempo, und die Kante spränge. Sie bleibt und hängt sich neu an.
            const folgt = a && c ? (a.quelle !== undefined ? enthaelt(c, a.quelle) : a.abstand !== undefined) : false;
            if (a && c && folgt) {
                const zeit = Math.max(0, a.quelle !== undefined ? zeitVon(c, a.quelle) : c.start + (a.abstand ?? 0));
                if (Math.abs(zeit - b[kante]) > 1e-9) {
                    b[kante] = zeit;
                    bewegt = true;
                }
                // Eine Lücke, die sich gefüllt hat: jetzt gibt es ein Bild, und
                // daran hängt die Kante ab hier -- so beim Teilen, wo die App erst
                // kürzt (die Kante steht kurz in einer Lücke) und dann das rechte
                // Stück anlegt.
                if (a.quelle === undefined) {
                    const besser = anker(t, b[kante], kante, b);
                    if (besser?.quelle !== undefined) {
                        if (kante === 'von')
                            b.vonAnker = besser;
                        else
                            b.bisAnker = besser;
                    }
                }
                continue;
            }
            // Weg oder ausserhalb: neu an das, was jetzt an der Kante steht.
            const neu = anker(t, b[kante], kante, b);
            if (kante === 'von')
                b.vonAnker = neu;
            else
                b.bisAnker = neu;
            if (!neu)
                delete b[kante === 'von' ? 'vonAnker' : 'bisAnker'];
        }
        if (b.bis < b.von + 0.04)
            b.bis = b.von + 0.04;
    }
    if (bewegt)
        t.exportbereiche.sort((a, b) => a.von - b.von);
}
//# sourceMappingURL=bereiche.js.map